import { ChevronDownIcon, Loader2Icon } from "lucide-react"
import type * as React from "react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import {
  FEATURE_FLAG_DEFAULTS,
  FEATURE_FLAG_LIVE_SOFT_CAP,
  isFeatureFlagKey,
  type FeatureFlagKey,
} from "@steelbill/types"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { DateField } from "@/components/DateField"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import {
  useFeatureFlags,
  useSetFeatureFlag,
  type FeatureFlagRow,
} from "@/features/settings/use-flags"
import { ApiError } from "@/lib/api-error"
import { useFormatDay } from "@/lib/dates"

/**
 * `/settings/features` — the feature flag screen (spec §18.1), behind
 * `settings.write`.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHAT THIS REPLACES: FOUR SSH SESSIONS AND FOUR HAND-WRITTEN `UPDATE`s AGAINST
 * A PRODUCTION TAX DATABASE. Both halves of the API — `GET /flags` and
 * `PUT /flags/:key`, audited, transactional, cache-invalidating — shipped with
 * nothing in front of either, and forty-odd translated `flags.*` strings sat in
 * `en.json` and `mr.json` that no `t()` call reached. The owner has four
 * switches to throw (credit notes, messages, notifications, the CA's login) and
 * until this screen every one of them was `UPDATE feature_flags SET enabled = 1
 * WHERE ...` typed into a shell that is one careless `WHERE` from a different
 * table.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * FIVE RULES IT EXISTS TO KEEP:
 *
 *  1. THE SCREEN WRITES THE ROW, AND THE ROW IS WHAT COUNTS. `flags.ts` governs
 *     a key that has never been seeded and nothing else; every deployed database
 *     has a row per key and `02_seed.sql` deliberately never rewrites `enabled`.
 *     Editing the code default therefore changes NOTHING on a live box — that is
 *     the evening of 2026-08-16, when `challans` was flipped in code, deployed,
 *     and the app went on saying "not switched on". `flags.storedRowWins` says
 *     so at the top of the screen, because the person reading it is the person
 *     who would otherwise go and edit the file.
 *
 *  2. TURNING ONE OFF IS AS CONSEQUENTIAL AS TURNING ONE ON, and the sentence
 *     naming what stops working is shown BEFORE the control moves. Nothing here
 *     is a switch that slides and then asks; the control is a BUTTON that opens
 *     the consequence and only then writes. Switching `invoices` off takes
 *     billing down for everyone on every device, and "are you sure?" is not a
 *     sentence that says so.
 *
 *  3. EVERY FLIP IS AUDITED, AND NOT BY THIS SCREEN. `setFeatureFlag` writes
 *     `feature_flag.update` with both images inside the write's own transaction
 *     (§13). A client-side "report this" call would be a second write that can
 *     fail alone, producing a trail with holes in exactly the rows somebody
 *     later disputes.
 *
 *  4. §18.4's OWNER AND REVIEW DATE ARE SURFACED, AND A FLAG PAST ITS DATE IS
 *     MARKED STALE — the server computes that in Asia/Kolkata (§13), so a date
 *     is never judged against the browser's midnight. The row a stale flag needs
 *     is the "extend" half of "remove or extend", so those two fields are
 *     editable exactly where they are actionable: on an unwritten row, where
 *     §18.4 requires them before the first switch, and on a stale one. Since
 *     2026-08-20 they live one fold down ({@link FlagDetails}) — which OPENS BY
 *     ITSELF in exactly those two cases, because a required field behind a
 *     disclosure is a disabled button with no visible reason, and a nag behind
 *     one is a nag nobody receives.
 *
 *  5. IT NAMES WHAT EACH FLAG GATES, IN THE READER'S OWN LANGUAGE.
 *     `flags.gatesOf.*` is the sentence written for a person and is the one line
 *     the row carries; the row's `description` is the §18.5 table's English
 *     "Gates" cell, technical, and sits in the details with the key.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE ROW IS NAME, WHAT IT SWITCHES, AND THE TOGGLE — the owner's ask on
 * 2026-08-20, in those words. It carried seven things before; nothing was
 * deleted from the DATA or the API (§18.4 needs all of it and the staleness nag
 * depends on it), only from what a row shows at rest. See {@link FlagDetails}.
 * ─────────────────────────────────────────────────────────────────────────────
 * NOT A SLIDING SWITCH, DELIBERATELY, AND THE COMPONENT EXISTS. A `Switch` is
 * controlled by the row it renders, so intercepting it to confirm first leaves
 * two bad options: let it slide and slide back (which reads as somebody else
 * having undone the change), or disable it (a control that never works). The
 * button carries the same one-tap meaning, states which direction it is going,
 * and cannot move before its consequence has been read — which is rule 2.
 *
 * NO FILTER, NO SEARCH, NO SORT. Thirteen rows in §18.5 table order, and the
 * order never changes: an operator's memory of where a switch was is worth more
 * on a list this size than any arrangement by state, and floating the stale ones
 * would move `invoices` around the page depending on the date.
 *
 * NO FEATURE FLAG ON THIS SCREEN OR ITS API, ever — a kill switch you can switch
 * off is not a kill switch. A 404 from `/flags` is a real 404 and `FeatureOff`
 * is the wrong panel for it.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function FeaturesScreen(): React.JSX.Element {
  const { t } = useTranslation()
  const flags = useFeatureFlags()

  const live = flags.data?.liveNonPermanent ?? 0
  const stale = flags.data?.stale ?? 0

  return (
    <div className="flex w-full flex-col gap-4 p-4 md:gap-6 md:p-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t("flags.title")}</h1>
        <p className="text-muted-foreground text-sm">{t("flags.screenHelp")}</p>
      </header>

      {/*
        * RULE 1, SAID FIRST AND SAID PLAINLY. The reader of this screen is the
        * one person who could instead go and edit `flags.ts` and deploy — and
        * discover a day later that nothing changed. This paragraph is the whole
        * reason the screen is worth building rather than documenting.
        */}
      <Alert>
        <AlertTitle>{t("flags.storedRowWinsTitle")}</AlertTitle>
        <AlertDescription>{t("flags.storedRowWins")}</AlertDescription>
      </Alert>

      <p className="text-muted-foreground text-sm">{t("flags.intro")}</p>

      {flags.isPending ? (
        <Skeleton className="h-96 w-full" />
      ) : flags.isError ? (
        <Alert variant="destructive">
          <AlertTitle>{t("err.couldNotLoad")}</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-3">
            <span>
              {flags.error instanceof ApiError
                ? flags.error.message
                : t("err.somethingWentWrong")}
            </span>
            <Button
              variant="outline"
              onClick={() => {
                void flags.refetch()
              }}
            >
              {t("btn.retry")}
            </Button>
          </AlertDescription>
        </Alert>
      ) : (
        <>
          {/*
            * §18.4's TWO COUNTS, BOTH COMPUTED BY THE SERVER over the list it
            * just sent. The cap is SOFT and nothing enforces it — a limit that
            * could refuse the eleventh toggle would refuse it on the day a
            * broken module had to go dark. It is a sentence, which is all it
            * was ever meant to be.
            */}
          <div className="flex flex-col gap-1">
            <p className="text-muted-foreground text-sm">
              {t("flags.liveCount", { live, cap: FEATURE_FLAG_LIVE_SOFT_CAP })}
            </p>
            {live > FEATURE_FLAG_LIVE_SOFT_CAP ? (
              <p className="text-sm">{t("flags.overCap", { cap: FEATURE_FLAG_LIVE_SOFT_CAP })}</p>
            ) : null}
            {stale > 0 ? (
              <p className="text-destructive text-sm">{t("flags.staleCount", { count: stale })}</p>
            ) : null}
          </div>

          <Card className="overflow-hidden py-0">
            <ul className="divide-border divide-y">
              {/* §18.5 table order — the server's, never re-sorted here. */}
              {(flags.data?.flags ?? []).map((flag) => (
                <FlagRow key={flag.key} flag={flag} />
              ))}
            </ul>
          </Card>
        </>
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Naming a flag for a human                                                   */
/* -------------------------------------------------------------------------- */

/**
 * The module's name in the reader's language, or the raw key when this build has
 * never heard of it.
 *
 * A LEFTOVER ROW FALLS BACK TO ITS KEY RATHER THAN TO A TRANSLATION MISS.
 * i18next renders the key itself for a missing entry, so asking it for
 * `flags.nameOf.` plus a key nobody defined would put that whole dotted string
 * on the screen — which reads as a broken app rather than as a row the code no
 * longer has. `isFeatureFlagKey` is the same narrowing the server's `isKnown` is
 * computed from, so the two agree about what "known" means.
 */
function useFlagName(): (key: string) => string {
  const { t } = useTranslation()
  return (key: string) => (isFeatureFlagKey(key) ? t(`flags.nameOf.${key}`) : key)
}

/* -------------------------------------------------------------------------- */
/* One flag                                                                    */
/* -------------------------------------------------------------------------- */

function FlagRow({ flag }: { flag: FeatureFlagRow }): React.JSX.Element {
  const { t } = useTranslation()
  const formatDay = useFormatDay()
  const flagName = useFlagName()

  /*
   * ONE MUTATION PER ROW, so a refusal on `payments` cannot paint an error under
   * `credit_notes`. The hook is called inside this component for exactly the
   * reason `UsersScreen` calls its pair per account.
   */
  const toggle = useSetFeatureFlag()

  /*
   * §18.4's two fields, editable where they are actionable.
   *
   * THEY START FROM THE ROW, so "extend" is a small edit of a date that is
   * already there rather than a blank the reader has to remember. An unwritten
   * row has neither, and the server REFUSES to create one without both (unless
   * the key is a permanent kill switch, which has no removal date to miss) —
   * §18.4 allows no unowned and no undated flag, because a flag nobody owns and
   * nobody can be nagged about is how a dead branch becomes permanent.
   */
  const [owner, setOwner] = useState(flag.owner ?? "")
  const [reviewBy, setReviewBy] = useState(flag.reviewBy ?? "")

  const name = flagName(flag.key)
  const known = isFeatureFlagKey(flag.key)
  /*
   * The §18.5 removal trigger, from the typed table rather than the row: the
   * seed deliberately does not copy it into the database, where a second copy
   * would drift from the one the code reads.
   */
  const removalNote = known ? FEATURE_FLAG_DEFAULTS[flag.key as FeatureFlagKey].removalNote : null

  /*
   * §18.4 AT CREATION, AND ONLY AT CREATION. An existing row is never
   * retro-required to gain a date on an unrelated toggle — switching a broken
   * module OFF must never fail validation, and the server takes the same view.
   */
  const needsOwner = !flag.isSeeded
  const needsReviewBy = !flag.isSeeded && !flag.isPermanent
  const detailsShown = !flag.isSeeded || flag.isStale

  const missingRequired =
    (needsOwner && owner.trim() === "") || (needsReviewBy && reviewBy.trim() === "")

  /** Only the keys the reader actually supplied — `FlagToggleSchema` is `.strict()`. */
  const body = (enabled: boolean) => ({
    enabled,
    ...(detailsShown && owner.trim() !== "" ? { owner: owner.trim() } : {}),
    ...(detailsShown && reviewBy.trim() !== "" ? { reviewBy } : {}),
  })

  const commit = (enabled: boolean) => {
    if (!known) return
    toggle.mutate({ key: flag.key as FeatureFlagKey, body: body(enabled) })
  }

  const ownerReview = t("flags.ownerReview", {
    owner: flag.owner ?? t("flags.noOwner"),
    reviewBy: flag.reviewBy === null ? t("flags.noReviewDate") : formatDay(flag.reviewBy),
  })

  return (
    <li className="flex flex-col gap-3 p-4" data-testid="flag-row">
      {/*
        ═══════════════════════════════════════════════════════════════════════
        THE ROW IS THREE THINGS (owner's ask, 2026-08-20): THE NAME, WHAT IT
        SWITCHES, AND THE CONTROL.

        It used to be seven — name, two or three badges, the gates sentence, the
        technical key with the §18.5 description, who owns it and when it is due
        for review, the removal trigger, and sometimes two form fields — stacked
        down the page for each of seventeen flags. Every one of those is a real
        §18.4 requirement and NONE OF THEM IS DELETED; they moved behind
        {@link FlagDetails} below. What was wrong was not that they existed but
        that they were shown to somebody whose question is "is Messages on?",
        seventeen times, so the toggle he came for sat five paragraphs down.

        THE NAME AND THE CONTROL ARE ON ONE LINE, so the answer to that question
        and the way to change it are the same glance. The gates sentence sits
        under the name because it is what the decision is made on.
        ═══════════════════════════════════════════════════════════════════════
      */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold">{name}</span>
            {/*
              THE ONE BADGE THAT SURVIVES ON THE ROW, and it survives because it
              is not decoration: a row the code no longer has must not look like
              a feature somebody could switch on. The ON/OFF badge went — the
              button beside it says "Turn off", which states the same fact and
              is the thing being reached for. `permanent` and `stale` moved into
              the details, where §18.4's dates and owner are; the STALE COUNT
              still shouts from the top of the screen and a stale row still
              opens its own details unprompted (see {@link FlagDetails}).
            */}
            {known ? null : <Badge variant="destructive">{t("flags.leftover")}</Badge>}
          </div>

          {/* WHAT IT SWITCHES, IN THE READER'S LANGUAGE — the sentence the
              decision is actually made on. */}
          {known ? (
            <p className="text-muted-foreground text-sm">{t(`flags.gatesOf.${flag.key}`)}</p>
          ) : null}
        </div>

        {known ? (
          <ToggleConfirm
            flag={flag}
            name={name}
            disabled={toggle.isPending || missingRequired}
            pending={toggle.isPending}
            onConfirm={() => {
              commit(!flag.enabled)
            }}
          />
        ) : null}
      </div>

      <FlagDetails
        flag={flag}
        name={name}
        known={known}
        ownerReview={ownerReview}
        removalNote={removalNote}
        openByDefault={detailsShown}
        owner={owner}
        reviewBy={reviewBy}
        onOwnerChange={setOwner}
        onReviewByChange={setReviewBy}
        saving={toggle.isPending}
        onSave={() => {
          commit(flag.enabled)
        }}
      />

      <ServerReply
        error={toggle.error}
        success={
          toggle.isSuccess
            ? t(toggle.data?.flag.enabled === true ? "flags.switchedOn" : "flags.switchedOff", {
                module: name,
              })
            : null
        }
      />
    </li>
  )
}

/* -------------------------------------------------------------------------- */
/* Everything §18.4 requires, one fold down                                    */
/* -------------------------------------------------------------------------- */

/**
 * The technical name, the §18.5 description, the owner, the review date, the
 * removal trigger — and the two fields that edit the first two of those.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * MOVED OFF THE ROW, NOT DELETED, AND THE DISTINCTION IS THE WHOLE DESIGN.
 *
 * §18.4 requires an owner and a review date on EVERY flag and the API enforces
 * it — `PUT /flags/:key` REFUSES to create a row without both, with a 400 that
 * quotes the rule. So a screen that simply dropped these fields would leave the
 * owner facing a "Turn on" button that returns a paragraph about §18.4 and no
 * way to satisfy it. They are one tap away instead.
 *
 * IT OPENS BY ITSELF IN THE TWO CASES WHERE HIDING IT WOULD BE A BUG:
 *
 *   NEVER SWITCHED HERE — the row has no stored `owner`/`review_by`, so the
 *   first toggle needs them and the button is disabled until they are typed. A
 *   disabled control whose reason is folded away is a control that appears
 *   broken.
 *
 *   STALE — §18.4's nag. The whole point of a review date is that somebody is
 *   made to look; a nag behind a disclosure is a nag nobody receives. The row
 *   arrives open, showing the passed date and the removal trigger, which is the
 *   "remove or extend" decision with both halves in front of the reader.
 *
 * That is also the answer to "where did the staleness warning go": the SCREEN's
 * stale COUNT is untouched at the top (`flags.staleCount`, destructive), and the
 * stale ROW opens itself. The soft-cap count is likewise untouched — it was
 * never row content.
 * ═════════════════════════════════════════════════════════════════════════════
 */
function FlagDetails({
  flag,
  name,
  known,
  ownerReview,
  removalNote,
  openByDefault,
  owner,
  reviewBy,
  onOwnerChange,
  onReviewByChange,
  saving,
  onSave,
}: {
  flag: FeatureFlagRow
  name: string
  known: boolean
  ownerReview: string
  removalNote: string | null
  openByDefault: boolean
  owner: string
  reviewBy: string
  onOwnerChange: (value: string) => void
  onReviewByChange: (value: string) => void
  saving: boolean
  onSave: () => void
}): React.JSX.Element {
  const { t } = useTranslation()
  /*
   * UNCONTROLLED AFTER MOUNT (`defaultOpen`), deliberately. A reader who folds
   * a stale row away having read it must not have it spring open again on the
   * next render — and this component re-renders on every keystroke in the owner
   * field.
   */
  return (
    <Collapsible defaultOpen={openByDefault}>
      <CollapsibleTrigger
        render={<Button variant="ghost" size="sm" className="-ml-2 self-start" />}
        data-testid="flag-details-trigger"
      >
        <ChevronDownIcon className="size-4" />
        {t("flags.detailsLabel")}
        {/* The module name out of sight, for the same reason the toggle carries
            it: seventeen buttons reading "Details" are one button to a screen
            reader, and voice control needs the visible words to stay inside the
            spoken ones. THE NAME, not the key — the key is inside the panel this
            opens, and putting it here too would make "credit_notes" ambiguous
            for anything looking the row up by its technical name. */}
        <span className="sr-only"> {name}</span>
      </CollapsibleTrigger>

      <CollapsibleContent className="flex flex-col gap-3 pt-3" data-testid="flag-details">
        <div className="flex flex-wrap items-center gap-2">
          {flag.enabled ? (
            <Badge variant="secondary">{t("flags.on")}</Badge>
          ) : (
            <Badge variant="outline">{t("flags.off")}</Badge>
          )}
          {/* §18.4 — surfaced as overdue, not merely as a date that has passed. */}
          {flag.isStale ? <Badge variant="destructive">{t("flags.stale")}</Badge> : null}
          {flag.isPermanent ? <Badge variant="outline">{t("flags.permanent")}</Badge> : null}
        </div>

        <p className="text-muted-foreground text-xs">
          <span className="font-medium">{t("flags.keyLabel")}</span>{" "}
          <span className="font-mono">{flag.key}</span>
          {" · "}
          {flag.description}
        </p>

        {/*
          * §18.4 — WHO IS ANSWERABLE AND WHEN TO LOOK AGAIN. An unwritten row
          * says so instead: it is running on the built-in setting, which is the
          * fact that explains why editing the code would have worked for it and
          * for nothing else on this screen.
          */}
        <p className="text-muted-foreground text-sm">
          {flag.isSeeded ? ownerReview : t("flags.neverSwitched")}
        </p>

        {flag.isPermanent ? (
          <p className="text-muted-foreground text-xs">{t("flags.permanentHelp")}</p>
        ) : removalNote === null ? null : (
          <p className="text-muted-foreground text-xs">
            <span className="font-medium">{t("flags.removalLabel")}</span> {removalNote}
          </p>
        )}

        {known ? (
          <div className="flex flex-col gap-3 border-t pt-3">
            {flag.isSeeded ? null : (
              <div>
                <p className="text-sm font-medium">{t("flags.firstSwitchTitle")}</p>
                <p className="text-muted-foreground text-xs">{t("flags.firstSwitchHelp")}</p>
              </div>
            )}

            <div className="grid gap-3 md:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={`owner-${flag.key}`}>{t("flags.owner")}</Label>
                <Input
                  id={`owner-${flag.key}`}
                  value={owner}
                  onChange={(event) => {
                    onOwnerChange(event.target.value)
                  }}
                />
                <p className="text-muted-foreground text-xs">{t("flags.ownerHelp")}</p>
              </div>

              {flag.isPermanent ? null : (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={`review-${flag.key}`}>{t("flags.reviewBy")}</Label>
                  {/*
                    * THE APP'S OWN CALENDAR, NEVER `<input type="date">` — the
                    * native control renders in the BROWSER's locale, so a
                    * Marathi screen opens an English picker and every platform
                    * draws a different thing.
                    */}
                  <DateField
                    id={`review-${flag.key}`}
                    value={reviewBy}
                    ariaLabel={t("flags.reviewBy")}
                    onChange={onReviewByChange}
                  />
                  <p className="text-muted-foreground text-xs">{t("flags.reviewByHelp")}</p>
                </div>
              )}
            </div>

            {/*
              * THE "EXTEND" HALF OF "STALE — REMOVE OR EXTEND", for a row that
              * already exists. It sends the flag's CURRENT state, so pushing a
              * review date out cannot switch a module on or off as a side
              * effect — `enabled` is required by the schema and this is the only
              * honest value to send with it.
              */}
            {flag.isSeeded ? (
              <div className="flex justify-end">
                <Button variant="outline" disabled={saving} onClick={onSave}>
                  {saving ? <Loader2Icon className="animate-spin" /> : null}
                  {t("btn.saveChanges")}
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}
      </CollapsibleContent>
    </Collapsible>
  )
}

/* -------------------------------------------------------------------------- */
/* The consequence, before the flag moves                                      */
/* -------------------------------------------------------------------------- */

/**
 * The one control that flips a flag, and the sentence that has to be read first.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * TURNING SOMETHING OFF NAMES WHAT STOPS WORKING. Not "are you sure?", which
 * asks the reader to supply the consequence out of their own head — the exact
 * moment they are least able to. `flags.blastOf.*` is one written sentence per
 * flag: switching `invoices` off stops billing for everyone on every device and
 * issues no numbers; switching `credit_notes` off leaves a wrong bill already
 * with the customer with no lawful correction; switching `accountant_role` off
 * ends the CA's open session on her next tap.
 *
 * AND IT SAYS "STRAIGHT AWAY, FOR EVERYONE, ON EVERY DEVICE". A flag is global
 * and the server drops its cache on commit, so the change is live on the next
 * request rather than at some next deploy. An operator who thinks this is a
 * local preference is an operator who tries one out.
 *
 * TURNING SOMETHING ON GETS A CONFIRMATION TOO, and deliberately: it is the same
 * global, immediate act, and `accountant_role` in particular hands an EXTERNAL
 * professional a session on a system holding the GSTIN, the PAN, the bank
 * details and every invoice ever issued.
 * ═════════════════════════════════════════════════════════════════════════════
 */
function ToggleConfirm({
  flag,
  name,
  disabled,
  pending,
  onConfirm,
}: {
  flag: FeatureFlagRow
  name: string
  disabled: boolean
  pending: boolean
  onConfirm: () => void
}): React.JSX.Element {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  const turningOff = flag.enabled

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger
        render={<Button variant={turningOff ? "outline" : "default"} disabled={disabled} />}
      >
        {t(turningOff ? "flags.turnOff" : "flags.turnOn")}
        {/*
          * THIRTEEN BUTTONS SAYING "Turn on" IS ONE BUTTON, AS FAR AS A SCREEN
          * READER IS CONCERNED. The module name is appended out of sight rather
          * than put in an `aria-label`, which would REPLACE the visible words:
          * an accessible name of "Turn Messages on or off" over a button that
          * reads "Turn on" fails "label in name" and leaves anybody using voice
          * control saying a phrase the button does not answer to. Appending
          * keeps the visible label inside the spoken one — "Turn on Messages".
          */}
        <span className="sr-only"> {name}</span>
      </AlertDialogTrigger>
      <AlertDialogContent className="sm:max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t(turningOff ? "flags.confirmOffTitle" : "flags.confirmOnTitle", { module: name })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t(turningOff ? "flags.confirmOffLead" : "flags.confirmOnLead")}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/*
          * THE BLAST RADIUS, IN A BOX OF ITS OWN so it reads as the substance of
          * the dialog rather than as small print under the question. Shown only
          * when switching OFF: `confirmOnLead` already carries the whole of what
          * turning one on does.
          */}
        {turningOff ? (
          <div className="border-destructive/40 bg-destructive/5 flex flex-col gap-1 rounded-md border p-3 text-sm">
            <span className="font-medium">{t("flags.blastLabel")}</span>
            <span>{t(`flags.blastOf.${flag.key}`)}</span>
          </div>
        ) : null}

        <AlertDialogFooter>
          <AlertDialogCancel render={<Button variant="outline" />}>
            {t("btn.cancel")}
          </AlertDialogCancel>
          <Button
            variant={turningOff ? "destructive" : "default"}
            disabled={pending}
            onClick={() => {
              onConfirm()
              setOpen(false)
            }}
          >
            {pending ? <Loader2Icon className="animate-spin" /> : null}
            {t(turningOff ? "flags.confirmOff" : "flags.confirmOn")}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

/* -------------------------------------------------------------------------- */
/* What the server said                                                        */
/* -------------------------------------------------------------------------- */

/**
 * The server's own words, unedited.
 *
 * THE TWO REFUSALS THAT MATTER HERE ARE 400s WITH A PARAGRAPH IN THEM: creating
 * a flag row with no owner, and creating one with no review date. Each names
 * §18.4, says why the rule exists ("a flag nobody owns becomes a dead branch
 * nobody deletes"; "a flag with no review date can never be surfaced as stale")
 * and spells the body it wanted. A friendlier local rewording would keep the
 * "no" and throw away every part that says what to do about it.
 *
 * A 5xx is the one substitution: `lib/api.ts` has already discarded the server's
 * message and there is nothing the reader can act on.
 */
function ServerReply({
  error,
  success,
}: {
  error: unknown
  success: string | null
}): React.JSX.Element | null {
  const { t } = useTranslation()

  if (error !== null && error !== undefined) {
    const apiError = error instanceof ApiError ? error : null

    return (
      <Alert variant="destructive">
        <AlertTitle>{t("err.couldNotSave")}</AlertTitle>
        <AlertDescription className="flex flex-col gap-1">
          <span>
            {apiError === null || apiError.isServerError
              ? t("err.somethingWentWrong")
              : apiError.message}
          </span>
          {apiError?.details.map((detail) => (
            <span key={`${detail.field}:${detail.message}`}>
              {detail.field}: {detail.message}
            </span>
          ))}
        </AlertDescription>
      </Alert>
    )
  }

  if (success === null) return null
  return <p className="text-muted-foreground text-sm">{success}</p>
}
